<template>
  <v-container v-if="$can('browse', 'Yacht')">
    <v-row
      no-gutters
      align="center"
      class="mb-6"
    >
      <v-col>
        <div class="headline">
          Yachts
        </div>
      </v-col>
      <v-col
        v-if="$can('create', 'Yacht')"
        class="shrink"
      >
        <v-btn
          color="primary"
          class="ma-0"
          @click="openYachtEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>

    <v-tabs
      background-color="transparent"
      class="ma-0"
    >
      <v-tab
        v-if="$can('browse-stocklist', 'Yacht')"
        class="text-none"
        :to="{ name: 'yachts', query: { view: 'sales' }}"
        exact
      >
        Order List
      </v-tab>
      <v-tab
        v-if="$can('browse-pending-logistics', 'Yacht')"
        class="text-none"
        :to="{ name: 'yachts', query: { view: 'pending-logistics' }}"
        exact
      >
        Pending Logistics Plan
      </v-tab>
      <v-tab
        v-if="$can('browse-charter', 'Yacht')"
        class="text-none"
        :to="{ name: 'yachts', query: { view: 'charter' }}"
        exact
      >
        In Charter
      </v-tab>
      <v-tab
        v-if="$can('browse-phased-out', 'Yacht')"
        class="text-none"
        :to="{ name: 'yachts', query: { view: 'phased-out' }}"
        exact
      >
        Phased Out
      </v-tab>
    </v-tabs>
    <v-divider class="mb-6" />

    <v-row>
      <v-col
        cols="3"
      >
        <div style="position: sticky; top: 20px">
          <v-subheader class="justify-space-between pr-0">
            Filters
            <v-btn
              v-if="isFiltered"
              text
              color="red"
              @click="clearFilters"
            >
              <v-icon
                small
                left
              >
                close
              </v-icon>
              Clear
            </v-btn>
          </v-subheader>
          <!--          <v-switch-->
          <!--            v-model="filtersDirty.my_yachts"-->
          <!--            label="My Yachts Only"-->
          <!--            dense-->
          <!--          />-->
          <v-text-field
            v-model="filtersDirty.id"
            solo
            dense
            type="number"
            label="ID"
            hide-details
            clearable
            class="mb-2"
            autocomplete="no"
          />
          <v-text-field
            v-model="filtersDirty.name"
            solo
            dense
            label="Name"
            hide-details
            clearable
            class="mb-2"
            autocomplete="no"
          />
          <v-text-field
            v-model="filtersDirty.hull_number"
            solo
            dense
            hide-details
            label="Hull Number"
            clearable
            class="mb-2"
          />
          <v-autocomplete
            v-model="filtersDirty.investment_status"
            solo
            multiple
            dense
            hide-details
            label="Sales Status"
            :items="allInvestmentStatuses"
            class="mb-2"
          />
          <v-autocomplete
            v-model="filtersDirty.status"
            solo
            dense
            multiple
            hide-details
            label="Logistics Status"
            :items="allLogisticsStatuses"
            class="mb-2"
          />
          <v-autocomplete
            v-if="$can('view', 'Owner')"
            v-model="filtersDirty.owner_id"
            solo
            hide-details
            dense
            label="Yacht owner"
            clearable
            :items="owners.data"
            :loading="owners.loading"
            item-value="id"
            item-text="name"
            class="mb-2"
          />
          <v-autocomplete
            v-model="filtersDirty.yacht_manufacturer_id"
            solo
            hide-details
            clearable
            label="Yacht Manufacturer"
            dense
            :items="manufacturers.data"
            :loading="manufacturers.loading"
            item-value="id"
            item-text="name"
            autocomplete="off"
            class="mb-2"
          />
          <yacht-model-select
            v-model="filtersDirty.yacht_model_id"
            solo
            hide-details
            label="Yacht Model"
            dense
            clearable
            :items="yachtModelsData"
            :loading="yachtModels.loading"
            class="mb-2"
          />
          <v-autocomplete
            v-model="filtersDirty.country_id"
            solo
            hide-details
            clearable
            label="Country"
            dense
            :items="countries.data"
            :loading="countries.loading"
            item-value="id"
            item-text="name"
            autocomplete="off"
            class="mb-2"
          />
          <v-text-field
            v-model="filtersDirty.delivery_year"
            solo
            type="number"
            label="Delivery Year"
            dense
            hide-details
            clearable
            class="mb-2"
          />
          <v-text-field
            v-model="filtersDirty.ex_factory_year"
            solo
            type="number"
            label="Ex Factory Year"
            dense
            hide-details
            clearable
            class="mb-2"
          />
          <v-text-field
            v-model="filtersDirty.phase_out_year"
            solo
            type="number"
            label="Phase Out Year"
            dense
            hide-details
            clearable
            class="mb-2"
          />
          <v-select
            v-model="filtersDirty.finance_model_type"
            class="mb-2"
            solo
            label="Investment Model"
            :items="financeModelTypes.data"
            :loading="financeModelTypes.loading"
            item-text="name"
            item-value="type"
            hide-details
            clearable
            dense
          />
        </div>
      </v-col>
      <v-col cols="13">
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="yachts.meta.from"
          :to="yachts.meta.to"
          :total="yachts.meta.total"
          :last-page="yachts.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
          has-actions
        >
          <template v-slot:action>
            <v-btn
              rounded
              text
              class="caption text-none ma-0"
              color="grey darken-2"
              @click="downloadXls()"
            >
              <v-icon
                left
                small
              >
                download
              </v-icon>
              Export to XLSX
            </v-btn>
          </template>
        </pagination>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="tableHeaders"
          :items="yachts.data"
          :loading="yachts.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="yachts.meta.total"
          must-sort
          hide-default-footer
          class="elevation-5"
        >
          <template v-slot:item="{ item }">
            <yacht-row
              v-if="currentView === 'drafts'"
              :yacht="item"
            />
            <stocklist-row
              v-else-if="currentView === 'sales' || currentView === 'pending-logistics'"
              :show-logistics="currentView === 'sales'"
              :yacht="item"
            />
            <in-charter-row
              v-else-if="currentView === 'charter' || currentView === 'phased-out'"
              :yacht="item"
            />
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <div
              v-if="isFiltered"
              class="text-center py-4"
            >
              <div>
                There are no results for your search.
              </div>
              <div
                class="mt-2"
              >
                <v-btn
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </div>
            </div>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <div
              v-else
              class="text-center py-4"
            >
              <v-col cols="16">
                There are no yachts at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  color="primary"
                  small
                  @click="openYachtEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </div>
            <!--</editor-fold>-->
          </template>
        </v-data-table>
        <!--</editor-fold>-->

        <pagination
          :from="yachts.meta.from"
          :to="yachts.meta.to"
          :total="yachts.meta.total"
          :last-page="yachts.meta.last_page"
          :page.sync="pagination.page"
          class="mt-1 align-center"
        />
      </v-col>
    </v-row>

    <yacht-edit-dialog
      v-if="$can('create', 'Yacht')"
      :visible.sync="yachtEditDialog"
      :is-sales="false"
      @update:yacht="yachtCreated"
    />
  </v-container>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import paginationMixin from '@/mixins/paginationMixin'
import filterableMixin from '@/mixins/filterableMixin'
import Owner from '@/models/Owner'
import Yacht from '@/models/Yacht'
import YachtModel from '@/models/YachtModel'
import YachtEditDialog from '@/components/business/yachts/YachtEditDialog'
import YachtRow from '@/components/business/yachts/YachtRow'
import StocklistRow from '@/components/business/yachts/StocklistRow'
import InCharterRow from '@/components/business/yachts/YachtInCharterRow'
import debounce from 'lodash/debounce'
import YachtModelSelect from '@/components/helpers/YachtModelSelect'
import FinanceModel from '@/models/FinanceModel'

export default {
    name: 'YachtsIndex',
    components: {
      YachtModelSelect,
      YachtRow,
      YachtEditDialog,
      StocklistRow,
      InCharterRow
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        yachts: {
          data: [],
          loading: false,
          meta: {}
        },
        yachtModels: {
          data: [],
          loading: false
        },
        owners: {
          data: [],
          loading: false
        },
        financeModelTypes: {
          data: [],
          loading: false
        },
        yachtEditDialog: false,
        filtersClean: Yacht.emptyFilters,
        filtersDirty: cloneDeep(Yacht.emptyFilters)
      }
    },
    computed: {
      currentView() {
        return this.$route.query.view
      },
      tableHeaders() {
        if (this.currentView === 'sales') {
          return [
            { text: 'ID', value: 'id', sortable: true },
            { text: 'Name', value: 'name', sortable: true },
            { text: 'Hull', value: 'hull_number', sortable: true },
            { text: 'Model', value: 'yacht_model_label', sortable: true },
            { text: 'Delivery Year', value: 'delivery_year', sortable: true },
            { text: 'Ex Factory', value: 'ex_factory_date', sortable: true },
            { text: 'Destination', value: 'current_base.name', sortable: false },
            { text: 'Sales', value: 'investment_status', sortable: false, width: '1%', class: 'px-0' },
            { text: 'Logistics', value: 'status', sortable: false, width: '1%', class: 'pl-2 pr-0' },
            ...this.$can('view', 'Owner') ? [{ text: 'Yacht owner', value: 'owner.name', sortable: false }] : [],
            ...this.$can('view-sale-price', 'Yacht') ? [{ text: 'Price', value: 'total_sale_price', sortable: false }] : []
          ]
        } else if (this.currentView === 'pending-logistics') {
          return [
            { text: 'ID', value: 'id', sortable: true },
            { text: 'Name', value: 'name', sortable: true },
            { text: 'Hull', value: 'hull_number', sortable: true },
            { text: 'Model', value: 'yacht_model_label', sortable: true },
            { text: 'Delivery Year', value: 'delivery_year', sortable: true },
            { text: 'Ex Factory', value: 'ex_factory_date', sortable: true },
            { text: 'Destination', value: 'current_base.name', sortable: false },
            { text: 'Sales', value: 'investment_status', sortable: false, width: '1%', class: 'px-0' },
            ...this.$can('view', 'Owner') ? [{ text: 'Yacht owner', value: 'owner.name', sortable: false }] : [],
            ...this.$can('view-sale-price', 'Yacht') ? [{ text: 'Price', value: 'total_sale_price', sortable: false }] : []
          ]
        } else if (this.currentView === 'sold') {
          return [
            { text: 'ID', value: 'id', sortable: true },
            { text: 'Name', value: 'name', sortable: true },
            { text: 'Hull', value: 'hull_number', sortable: true },
            { text: 'Model', value: 'yacht_model_label', sortable: true },
            { text: 'Delivery Year', value: 'delivery_year', sortable: true },
            { text: 'Ex Factory', value: 'ex_factory_date', sortable: true },
            { text: 'Destination', value: 'current_base.name', sortable: false },
            { text: 'Logistics', value: 'status', sortable: false, width: '1%', class: 'px-0' },
            ...this.$can('view', 'Owner') ? [{ text: 'Yacht owner', value: 'owner.name', sortable: false }] : [],
            ...this.$can('view-sale-price', 'Yacht') ? [{ text: 'Price', value: 'total_sale_price', sortable: false }] : []
          ]
        } else if (this.currentView === 'charter' || this.currentView === 'phased-out') {
          return [
            { text: 'ID', value: 'id', sortable: true },
            { text: 'Name', value: 'name', sortable: true },
            { text: 'Hull', value: 'hull_number', sortable: true },
            { text: 'Model', value: 'yacht_model_label', sortable: true },
            { text: 'Model Year', value: 'production_year', sortable: true },
            { text: 'Base', value: 'current_base.name', sortable: false },
            { text: 'Sales', value: 'investment_status', sortable: false, width: '1%', class: 'px-0' },
            ...this.$can('view', 'Owner') ? [{ text: 'Yacht owner', value: 'owner.name', sortable: false }] : []
          ]
        } else {
          return [
            { text: 'ID', value: 'id', sortable: true },
            { text: 'Name', value: 'name', sortable: true },
            { text: 'Hull', value: 'hull_number', sortable: false },
            { text: 'Country', value: 'country_id', sortable: false },
            { text: 'Model', value: 'yacht_model_label', sortable: true },
            ...this.$can('view-sale-price', 'Yacht') ? [{ text: 'Price', value: 'total_sale_price', sortable: false }] : [],
            { text: 'Sales', value: 'investment_status', sortable: false }
          ]
        }
      },
      countries() {
        return this.$store.getters['settings/charteringCountries']
      },
      manufacturers() {
        return this.$store.getters['settings/yachtManufacturers']
      },
      yachtModelsData() {
        return this.yachtModels.data.filter(ym => {
          if (!this.filtersDirty.yacht_manufacturer_id) return true

          return this.filtersDirty.yacht_manufacturer_id === ym.yacht_manufacturer_id
        })
      },
      allInvestmentStatuses() {
        return Yacht.allInvestmentStatuses()
      },
      allLogisticsStatuses() {
        return Yacht.allStatuses()
      }
    },
    watch: {
      currentView: function (view) {
        if (view === 'sales') {
          this.sorting.sortBy = 'ex_factory_date'
          this.sorting.desc = false
        }

        this.setFilters()
      }
    },
    mounted() {
      let allowedViews = [
        ...this.$can('browse-stocklist', 'Yacht') ? ['sales'] : [],
        ...this.$can('browse-pending-logistics', 'Yacht') ? ['pending-logistics'] : [],
        ...this.$can('browse-charter', 'Yacht') ? ['charter'] : []
      ]

      const routeView = this.$route.query.view

      if (!routeView || allowedViews.indexOf(routeView) === -1) {
        this.$router.replace({
          name: 'yachts',
          query: {
            view: allowedViews[0]
          }
        })
      }

      this.setFilters()

      this.loadQuery()
      this.fetchYachts()
      this.fetchOwners()
      this.fetchYachtModels()
      this.fetchFinanceModelTypes()
    },
    methods: {
      setFilters() {
        if (this.currentView === 'sales') {
          this.filtersDirty.status = [ Yacht.STATUS_PENDING, Yacht.STATUS_PLANNED, Yacht.STATUS_DELIVERY ]
          this.filtersDirty.investment_status = [ Yacht.INVESTMENT_STATUS_DRAFT, Yacht.INVESTMENT_STATUS_SALES, Yacht.INVESTMENT_STATUS_APPROVAL, Yacht.INVESTMENT_STATUS_APPROVED, Yacht.INVESTMENT_STATUS_RESERVED, Yacht.INVESTMENT_STATUS_SOLD ]
        } else if (this.currentView === 'pending-logistics') {
          this.filtersDirty.status = [ Yacht.STATUS_PENDING ]
          this.filtersDirty.investment_status = undefined
        } else if (this.currentView === 'charter') {
           this.filtersDirty.status = [ Yacht.STATUS_IN_USE ]
           this.filtersDirty.investment_status = undefined
        } else if (this.currentView === 'phased-out') {
           this.filtersDirty.status = [ Yacht.STATUS_PHASED_OUT ]
           this.filtersDirty.investment_status = undefined
        }
      },
      loadQuery() {
        Object.keys(this.$route.query).map(key => {
          this.filtersDirty[key] = this.$route.query[key]
        })
      },
      onPaginationUpdate() {
        this.fetchYachts()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      fetchYachts: debounce(async function() {
        this.yachts.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        try {
          let result = await Yacht.api.fetchYachts(queryParams)

          this.yachts.data = result.data
          this.yachts.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachts.loading = false
        }
      }, 500),
      async fetchYachtModels() {
        this.yachtModels.loading = true
        try {
          this.yachtModels.data = await YachtModel.api.fetch({
            status: YachtModel.STATUS_ACTIVE
          })
        } catch (error) {

        } finally {
          this.yachtModels.loading = false
        }
      },
      async fetchOwners() {
        if (!this.$can('view', 'Owner')) return

        this.owners.loading = true

        try {
          this.owners.data = await Owner.api.getOwners()
        } catch (error) {
          throw error
        } finally {
          this.owners.loading = false
        }
      },
      async fetchFinanceModelTypes() {
        this.financeModelTypes.loading = true

        this.financeModelTypes.data = await FinanceModel.api.getTypes()

        this.financeModelTypes.loading = false
      },
      yachtCreated(yacht) {
        this.$router.push({ name: 'yachtDashboard', params: { id: yacht.id } })
      },
      openYachtEditDialog() {
        this.yachtEditDialog = true
      },
      async downloadXls() {
        let queryParams = {
          ...this.filterQueryParams
        }

        try {
          let result = await Yacht.api.downloadXls(queryParams)

          window.open(result.path, '_blank')
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        }
      }
    }
  }
</script>
